import { useState, useEffect, useRef, useCallback, useMemo } from "react";

import ReportMarkdown from "./ReportMarkdown";
import CitationFooter from "./CitationFooter";
import { extractAnnotations } from "./utils/reportUtils";

const ReportDocument = ({
  title,
  content,
  citations,
  streamingContent,
  isStreaming,
  isUndoing,
  undoTargetContent,
  undoTargetTitle,
  undoTargetCitations,
  onUndoComplete,
  reportOptions,
  onSourceClick,
  activeSourceId,
  citationInteractions,
  selectedEdit,
  onTextSelection,
}) => {
  const scrollRef = useRef(null);
  const contentRef = useRef(null);
  const userScrolledRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  const lastContentHeightRef = useRef(0);

  // ── Undo animation ────────────────────────────────────────────────────
  const [animContent, setAnimContent] = useState(null);
  const [animPhase, setAnimPhase] = useState(null);
  const eraseRef = useRef(null);
  const typeRef = useRef(null);

  useEffect(() => {
    if (!isUndoing) return;
    const source = content || "";
    let idx = source.length;
    setAnimContent(source);
    setAnimPhase("erasing");
    eraseRef.current = setInterval(() => {
      idx -= 12;
      if (idx <= 0) {
        clearInterval(eraseRef.current);
        eraseRef.current = null;
        setAnimContent("");
        setAnimPhase("typing");
      } else setAnimContent(source.slice(0, idx));
    }, 8);
    return () => {
      if (eraseRef.current) clearInterval(eraseRef.current);
      if (typeRef.current) clearInterval(typeRef.current);
    };
  }, [isUndoing, content]);

  useEffect(() => {
    if (animPhase !== "typing") return;
    const target = undoTargetContent || "";
    if (!target.length) {
      setAnimContent("");
      setAnimPhase(null);
      onUndoComplete?.();
      return;
    }
    let idx = 0;
    typeRef.current = setInterval(() => {
      idx += 8;
      if (idx >= target.length) {
        clearInterval(typeRef.current);
        typeRef.current = null;
        setAnimContent(null);
        setAnimPhase(null);
        onUndoComplete?.();
      } else setAnimContent(target.slice(0, idx));
    }, 8);
    return () => {
      if (typeRef.current) clearInterval(typeRef.current);
    };
  }, [animPhase, undoTargetContent, onUndoComplete]);

  /* Scroll stability: keep viewport anchored during content growth */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const newHeight = el.scrollHeight;
    const heightDelta = newHeight - lastContentHeightRef.current;
    if (heightDelta > 0 && lastScrollTopRef.current > 0) {
      el.scrollTop = lastScrollTopRef.current + heightDelta;
    }
    lastContentHeightRef.current = newHeight;
  }, [streamingContent, animContent, isStreaming, animPhase, content]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    lastScrollTopRef.current = el.scrollTop;
    lastContentHeightRef.current = el.scrollHeight;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    userScrolledRef.current = !atBottom;
  }, []);

  const displayCitations = isUndoing ? [] : citations;

  let displayContent = "";
  let displayStreaming = false;
  if (animContent !== null) {
    displayContent = animContent;
    displayStreaming = true;
  } else if (isStreaming && streamingContent) {
    displayContent = streamingContent;
    displayStreaming = true;
  } else {
    displayContent = content;
    displayStreaming = false;
  }

  const showLineNumbers = reportOptions?.line_numbers || false;

  const annotations = useMemo(
    () => extractAnnotations(displayContent),
    [displayContent],
  );

  const hasContent = displayContent && displayContent.trim().length > 0;

  const cleanContent = useMemo(() => {
    if (!displayContent) return "";
    return displayContent.replace(/<!--\s*@pagebreak\s*-->/gi, "\n\n---\n\n");
  }, [displayContent]);

  const getSectionHeading = useCallback((markdown, start) => {
    const before = markdown.slice(0, Math.max(0, start));
    const headings = before.match(/^#{1,6}\s+.+$/gm);
    return headings?.[headings.length - 1]?.trim() || "";
  }, []);

  const buildSelectionPayload = useCallback(() => {
    const selection = window.getSelection?.();
    const contentEl = contentRef.current;
    if (!selection || selection.isCollapsed || !contentEl) return null;
    if (
      !contentEl.contains(selection.anchorNode) ||
      !contentEl.contains(selection.focusNode)
    ) {
      return null;
    }

    const selectedText = selection.toString().trim();
    if (!selectedText || selectedText.length > 20000) return null;

    const source = content || "";
    const attachRawRange = (start, end) => {
      const rawText = source.slice(start, end);
      return {
        text: rawText,
        display_text: selectedText,
        mapped: true,
        start,
        end,
        section_heading: getSectionHeading(source, start),
        prefix: source.slice(Math.max(0, start - 500), start),
        suffix: source.slice(end, end + 500),
      };
    };

    const getElement = (node) =>
      node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement;
    const closestBlock = (node) =>
      getElement(node)?.closest?.("[data-report-block]") || null;
    const findOccurrences = (value, needle) => {
      if (!value || !needle) return [];
      const indexes = [];
      let offset = 0;
      while (offset <= value.length) {
        const index = value.indexOf(needle, offset);
        if (index < 0) break;
        indexes.push(index);
        offset = index + Math.max(needle.length, 1);
      }
      return indexes;
    };
    const findOccurrencesInsensitive = (value, needle) =>
      findOccurrences(value.toLowerCase(), needle.toLowerCase());
    const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

    // Prefer the rendered markdown block that owns the browser selection. This
    // lets one-word selections map safely even when the word appears elsewhere
    // in the report, because we search within the paragraph/heading raw range.
    if (range) {
      const startBlock = closestBlock(range.startContainer);
      const endBlock = closestBlock(range.endContainer);
      if (startBlock && startBlock === endBlock) {
        const rawStart = Number(startBlock.dataset.reportRawStart);
        const rawEnd = Number(startBlock.dataset.reportRawEnd);
        if (Number.isFinite(rawStart) && Number.isFinite(rawEnd) && rawEnd > rawStart) {
          const blockRaw = source.slice(rawStart, rawEnd);
          const exactInBlock = findOccurrences(blockRaw, selectedText);
          if (exactInBlock.length === 1) {
            return attachRawRange(rawStart + exactInBlock[0], rawStart + exactInBlock[0] + selectedText.length);
          }
          if (exactInBlock.length > 1) {
            const beforeRange = range.cloneRange();
            beforeRange.selectNodeContents(startBlock);
            beforeRange.setEnd(range.startContainer, range.startOffset);
            const renderedBefore = beforeRange.toString();
            const occurrenceOrdinal = findOccurrencesInsensitive(renderedBefore, selectedText).length;
            const selectedIndex = exactInBlock[occurrenceOrdinal];
            if (Number.isFinite(selectedIndex)) {
              return attachRawRange(rawStart + selectedIndex, rawStart + selectedIndex + selectedText.length);
            }
          }
        }
      }
    }

    const first = source.indexOf(selectedText);
    const last = source.lastIndexOf(selectedText);
    if (first >= 0 && first === last) {
      return attachRawRange(first, first + selectedText.length);
    }

    // Rendered markdown often normalizes line breaks/spacing. Build a compact
    // raw-markdown index so those safe cases still map to an exact raw range.
    const normalizeWithMap = (value) => {
      let normalized = "";
      const map = [];
      let inWhitespace = false;
      for (let index = 0; index < value.length; index += 1) {
        const char = value[index];
        if (/\s/.test(char)) {
          if (!inWhitespace && normalized.length > 0) {
            normalized += " ";
            map.push(index);
          }
          inWhitespace = true;
        } else {
          normalized += char.toLowerCase();
          map.push(index);
          inWhitespace = false;
        }
      }
      return { normalized: normalized.trim(), map };
    };

    const raw = normalizeWithMap(source);
    const selected = selectedText.replace(/\s+/g, " ").trim().toLowerCase();
    const normalizedFirst = raw.normalized.indexOf(selected);
    const normalizedLast = raw.normalized.lastIndexOf(selected);
    if (normalizedFirst >= 0 && normalizedFirst === normalizedLast) {
      const rawStart = raw.map[normalizedFirst];
      const rawEnd = raw.map[normalizedFirst + selected.length - 1] + 1;
      if (Number.isFinite(rawStart) && Number.isFinite(rawEnd) && rawEnd > rawStart) {
        return attachRawRange(rawStart, rawEnd);
      }
    }

    const repeated =
      first >= 0 && last >= 0 && first !== last ||
      normalizedFirst >= 0 && normalizedLast >= 0 && normalizedFirst !== normalizedLast;
    return {
      text: selectedText,
      display_text: selectedText,
      mapped: false,
      unmapped_reason: repeated
        ? "That text appears more than once, so I can't safely identify which instance to edit. Try selecting a few surrounding words or use the paragraph/section ⋮ handle."
        : "This selection could not be mapped safely. Try selecting plain text without citation buttons, or use the paragraph/section ⋮ handle.",
    };
  }, [content, getSectionHeading]);

  const captureSelection = useCallback((event) => {
    if (event?.target?.closest?.("[data-report-block-handle]")) return;
    window.setTimeout(() => {
      const payload = buildSelectionPayload();
      if (payload) onTextSelection?.(payload);
    }, 0);
  }, [buildSelectionPayload, onTextSelection]);

  return (
    <div className="relative flex h-full flex-col bg-mainBg">
      {selectedEdit?.text && (
        <div data-report-ui="true" className="pointer-events-none absolute left-1/2 top-3 z-20 -translate-x-1/2 rounded-full border border-velBlue/25 bg-mainBg/95 px-3 py-1 text-[11px] text-velBlue shadow-sm backdrop-blur-sm">
          Text selected for edit
        </div>
      )}
      <div className="flex-shrink-0 pt-10" />

      <div
        className="min-h-0 flex-1 overflow-y-auto"
        ref={scrollRef}
        onScroll={handleScroll}
        onMouseUp={captureSelection}
        onKeyUp={captureSelection}
      >
        {!hasContent ? (
          <div className="h-full flex flex-col items-center justify-center px-8 text-center">
            {!isStreaming && !isUndoing && (
              <p className="text-[13px] leading-relaxed text-mutedText">
                Start a conversation to begin building your report.
              </p>
            )}
          </div>
        ) : (
          <div className="flex justify-center px-8 pb-14">
            <div
              className={[
                "relative w-full max-w-[860px] rounded-xl border border-surfaceBorder",
                "bg-surface/70 shadow-login-panel backdrop-blur-sm",
                showLineNumbers ? "px-6 py-10" : "px-10 py-10 lg:px-12",
              ].join(" ")}
            >
              {/* Line numbers gutter */}
              {showLineNumbers && !displayStreaming && (
                <div className="pointer-events-none absolute bottom-10 left-0 top-10 flex w-10 select-none flex-col overflow-hidden border-r border-mainDivider pr-2 pt-0.5 text-right">
                  {cleanContent.split("\n").map((_, li) => (
                    <span
                      key={li}
                      className="font-mono text-[10px] leading-[1.8] text-mutedText/40"
                    >
                      {(reportOptions?.start_line || 1) + li}
                    </span>
                  ))}
                </div>
              )}

              <div className={showLineNumbers ? "ml-8" : ""}>
                <div
                  ref={contentRef}
                  data-report-content
                  className="text-[15px] leading-[1.8] text-mainText"
                >
                  <ReportMarkdown
                    text={cleanContent}
                    citations={displayCitations}
                    streaming={displayStreaming}
                    onSourceClick={onSourceClick}
                    activeSourceId={activeSourceId}
                    citationInteractions={citationInteractions}
                    sourceText={content}
                    onBlockSelection={onTextSelection}
                  />
                  {displayStreaming && displayContent && (
                    <span className="ml-0.5 inline-block h-[18px] w-[3px] animate-pulse rounded-full bg-velBlue align-middle" />
                  )}
                  {!displayStreaming && displayCitations.length > 0 && (
                    <CitationFooter
                      citations={displayCitations}
                      onSourceClick={onSourceClick}
                      activeSourceId={activeSourceId}
                      citationInteractions={citationInteractions}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Margin annotations overlay */}
            {annotations.length > 0 && !displayStreaming && (
              <div className="fixed right-4 top-20 w-48 space-y-2 z-10 hidden xl:block">
                {annotations.map((a, i) => (
                  <div
                    key={i}
                    className="rounded border border-yellow-500/20 bg-yellow-500/10 px-2.5 py-1.5 text-[11px] text-yellow-500"
                  >
                    {a.text}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportDocument;
